<template>
  <Card>
    <CardHeader>
      <CardTitle>3. MIDs &amp; channels</CardTitle>
      <CardDescription>
        Add the channels (and their merchant IDs) where this offer should match transactions.
      </CardDescription>
    </CardHeader>

    <CardContent class="space-y-6">
      <!-- Existing channels -->
      <div v-if="loadingChannels" class="flex items-center justify-center py-8">
        <Loader2 class="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
      <div v-else-if="channels.length" class="space-y-2">
        <Label>Channels for this retailer</Label>
        <div class="divide-y divide-border rounded-lg border border-border">
          <div
            v-for="c in channels"
            :key="c.id"
            class="flex items-center gap-3 px-4 py-3"
          >
            <Antenna class="w-4 h-4 text-muted-foreground shrink-0" />
            <div class="min-w-0">
              <p class="text-sm font-medium truncate">{{ c.channelName }}</p>
              <p class="text-xs text-muted-foreground truncate">
                {{ c.channelType }} ·
                {{ c.mids && c.mids.length ? c.mids.length + ' MID(s)' : 'No MIDs' }}
              </p>
            </div>
            <Badge variant="secondary" class="ml-auto shrink-0">Saved</Badge>
            <Button
              variant="ghost"
              size="icon"
              class="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
              :disabled="deletingId === c.id"
              @click="deleteChannel(c)"
            >
              <Loader2 v-if="deletingId === c.id" class="w-4 h-4 animate-spin" />
              <Trash2 v-else class="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
      <p v-else class="text-sm text-muted-foreground">No channels added yet.</p>

      <!-- Add channel form -->
      <div class="rounded-lg border border-border p-4 space-y-4">
        <p class="text-sm font-medium">Add a channel</p>
        <div class="grid gap-4 sm:grid-cols-2">
          <div class="space-y-1.5">
            <Label>Channel name</Label>
            <Input v-model="form.channelName" placeholder="e.g. Online store" />
          </div>
          <div class="space-y-1.5">
            <Label>Channel type</Label>
            <Select v-model="form.channelType">
              <SelectTrigger class="w-full">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="t in channelTypes" :key="t" :value="t">{{ t }}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div class="space-y-1.5">
            <Label>Website</Label>
            <Input v-model="form.website" placeholder="https://example.com" />
          </div>
          <div class="space-y-1.5">
            <Label>Address</Label>
            <Input v-model="form.address" placeholder="Street address" />
          </div>
          <div class="space-y-1.5">
            <Label>City</Label>
            <Input v-model="form.city" placeholder="City" />
          </div>
          <div class="space-y-1.5">
            <Label>Postcode</Label>
            <Input v-model="form.postcode" placeholder="Postcode" />
          </div>
        </div>

        <div class="space-y-1.5">
          <Label>MIDs</Label>
          <div class="flex gap-2">
            <Input
              v-model="midInput"
              placeholder="Enter a MID and press Add"
              @keyup.enter="addMid"
            />
            <Button variant="secondary" type="button" class="h-10 shrink-0" @click="addMid">Add</Button>
            <Button
              variant="outline"
              type="button"
              class="h-10 shrink-0"
              @click="$refs.midFile.click()"
            >
              <Upload class="w-4 h-4" />
              Upload
            </Button>
            <input ref="midFile" type="file" accept=".csv,.txt" class="hidden" @change="onMidFile" />
          </div>
          <p class="text-xs text-muted-foreground">
            Add MIDs one at a time, or upload a .csv/.txt file (one MID per line or comma-separated).
          </p>
          <div v-if="form.mids.length" class="flex flex-wrap gap-1.5 pt-1">
            <Badge
              v-for="(mid, i) in form.mids"
              :key="mid + i"
              variant="outline"
              class="gap-1 cursor-pointer"
              @click="form.mids.splice(i, 1)"
            >
              {{ mid }}
              <X class="w-3 h-3" />
            </Badge>
          </div>
        </div>

        <div>
          <Button variant="outline" :disabled="!canAddChannel || addingChannel" @click="addChannel">
            <Loader2 v-if="addingChannel" class="w-4 h-4 animate-spin" />
            <Plus v-else class="w-4 h-4" />
            Add channel
          </Button>
        </div>
      </div>

      <p v-if="error" class="text-sm text-destructive">{{ error }}</p>
    </CardContent>

    <CardFooter class="justify-between gap-2">
      <Button variant="outline" @click="back">
        <MoveLeft class="w-4 h-4" /> Back
      </Button>
      <Button :disabled="!channels.length" @click="next">
        Next stage
        <MoveRight class="w-4 h-4" />
      </Button>
    </CardFooter>
  </Card>
</template>

<script>
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Button,
  Input,
  Label,
  Badge,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@boogi/ui'
import { Antenna, Plus, X, MoveRight, MoveLeft, Loader2, Upload, Trash2 } from 'lucide-vue-next'
import { addOfferWizard } from '@/stores/addOfferWizard'
import { wizardApi } from './wizardApi'

export default {
  name: 'step-mids',
  mixins: [wizardApi],
  components: {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardFooter,
    Button,
    Input,
    Label,
    Badge,
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
    Antenna,
    Plus,
    X,
    MoveRight,
    MoveLeft,
    Loader2,
    Upload,
    Trash2,
  },
  data() {
    return {
      wizard: addOfferWizard,
      channelTypes: ['ONLINE', 'INSTORE', 'ANY'],
      channels: [],
      loadingChannels: false,
      addingChannel: false,
      deletingId: '',
      midInput: '',
      error: '',
      form: this.emptyForm(),
    }
  },
  computed: {
    canAddChannel() {
      return this.form.channelName.trim() !== ''
    },
  },
  created() {
    if (!this.wizard.retailer.id) {
      this.$router.replace('/add-offer/retailer')
      return
    }
    this.loadChannels()
  },
  methods: {
    emptyForm() {
      return {
        channelName: '',
        channelType: 'ONLINE',
        address: '',
        city: '',
        postcode: '',
        website: '',
        mids: [],
      }
    },
    async loadChannels() {
      this.loadingChannels = true
      try {
        const page = await this.$api(
          this.byooPath(`/retailer/${this.wizard.retailer.id}/channel?page=0&size=100`)
        )
        this.channels = page?.content || []
        this.wizard.channels = this.channels.map((c) => c.id)
      } catch (e) {
        this.error = e.message
      } finally {
        this.loadingChannels = false
      }
    },
    addMid() {
      const mid = this.midInput.trim()
      if (mid && !this.form.mids.includes(mid)) this.form.mids.push(mid)
      this.midInput = ''
    },
    // Bulk-add MIDs from an uploaded document (one MID per line or comma/space/semicolon separated).
    async onMidFile(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = '' // allow re-uploading the same file
      if (!file) return
      try {
        const text = await file.text()
        const parsed = text
          .split(/[\s,;]+/)
          .map((m) => m.trim())
          .filter(Boolean)
        let added = 0
        for (const mid of parsed) {
          if (!this.form.mids.includes(mid)) {
            this.form.mids.push(mid)
            added++
          }
        }
        if (!added) this.error = 'No new MIDs found in that file.'
      } catch (e) {
        this.error = 'Could not read that file.'
      }
    },
    async deleteChannel(channel) {
      if (!channel || !channel.id || this.deletingId) return
      if (!window.confirm(`Delete channel "${channel.channelName}"? This cannot be undone.`)) return
      this.error = ''
      this.deletingId = channel.id
      try {
        await this.$api(this.byooPath(`/channel/${channel.id}`), { method: 'DELETE' })
        this.channels = this.channels.filter((c) => c.id !== channel.id)
        this.wizard.channels = this.channels.map((c) => c.id)
      } catch (e) {
        this.error = e.message
      } finally {
        this.deletingId = ''
      }
    },
    async addChannel() {
      if (!this.canAddChannel) return
      this.error = ''
      this.addingChannel = true
      try {
        const channel = await this.$api(this.byooPath('/channel'), {
          method: 'POST',
          body: {
            channelId: null,
            retailerId: this.wizard.retailer.id,
            channelName: this.form.channelName.trim(),
            channelType: this.form.channelType,
            address: this.form.address.trim(),
            city: this.form.city.trim(),
            postcode: this.form.postcode.trim(),
            website: this.form.website.trim(),
            mids: this.form.mids,
          },
        })
        if (channel) {
          this.channels.push(channel)
          this.wizard.channels = this.channels.map((c) => c.id)
        }
        this.form = this.emptyForm()
      } catch (e) {
        this.error = e.message
      } finally {
        this.addingChannel = false
      }
    },
    back() {
      this.$router.push(
        this.wizard.selectedRetailer ? '/add-offer/retailer' : '/add-offer/transaction'
      )
    },
    next() {
      if (!this.channels.length) return
      this.wizard.channelsConfirmed = true
      this.$router.push('/add-offer/info')
    },
  },
}
</script>
